import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, PlusSquare, ListOrdered, Barcode, BarChart3, LogOut, Menu, X, Shield, Database } from "lucide-react";
import { COMPANY } from "@/lib/company";

const NAV = [
  { to: "/dashboard", label: "DASHBOARD", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/orders/new", label: "NEW ORDER", icon: PlusSquare, tid: "nav-new-order" },
  { to: "/orders", label: "ORDERS", icon: ListOrdered, tid: "nav-orders" },
  { to: "/barcodes", label: "BARCODES", icon: Barcode, tid: "nav-barcodes" },
  { to: "/reports", label: "REPORTS", icon: BarChart3, tid: "nav-reports" },
];
const ADMIN_NAV = [
  { to: "/admin/masters", label: "MASTERS", icon: Database, tid: "nav-masters" },
  { to: "/admin/users", label: "USERS", icon: Shield, tid: "nav-admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const nav = user?.role === "admin" ? [...NAV, ...ADMIN_NAV] : NAV;

  return (
    <div className="min-h-screen bg-[#F4F4F0]">
      {/* Top bar */}
      <header className="border-b-2 border-black bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/dashboard")} data-testid="brand-logo">
            <img src={COMPANY.logoUrl} alt="MKB" className="w-10 h-10 border-2 border-black bg-white object-contain p-0.5" />
            <div className="font-heading font-black tracking-tight text-lg leading-none">
              {COMPANY.appName}
              <div className="text-[9px] uppercase tracking-widest text-[#666] font-normal mt-0.5">MKB Designer Studio</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `px-3 py-2 text-xs uppercase tracking-wider font-bold border-2 ${
                    isActive ? "bg-[#0A0A0A] text-white border-black" : "border-transparent hover:border-black"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 border-2 border-black" />}
              <div className="text-xs uppercase tracking-wider font-bold max-w-[120px] truncate">{user?.name}</div>
            </div>
            <button onClick={logout} data-testid="logout-btn" className="brut-btn brut-btn-secondary !py-2 !px-3 text-xs">
              <LogOut size={14} strokeWidth={3} />
            </button>
            <button onClick={() => setOpen(!open)} className="md:hidden brut-btn brut-btn-secondary !py-2 !px-3" data-testid="mobile-menu-btn">
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t-2 border-black bg-white">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`m-${n.tid}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 border-b border-black/20 ${
                    isActive ? "bg-[#0A0A0A] text-white" : ""
                  }`
                }
              >
                <n.icon size={18} strokeWidth={2.5} />
                <span className="text-sm font-bold uppercase tracking-wider">{n.label}</span>
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
        <Outlet />
      </main>

      {/* Company footer (visible on every authenticated page) */}
      <footer className="border-t-2 border-black bg-white mt-10" data-testid="company-footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 grid sm:grid-cols-[auto_1fr_auto] gap-5 items-start">
          <img src={COMPANY.logoUrl} alt="MKB" className="w-14 h-14 border-2 border-black bg-white object-contain p-1" />
          <div>
            <div className="font-heading font-black text-base tracking-tight">{COMPANY.legalName}</div>
            <div className="text-xs text-[#666] mt-1 leading-relaxed">{COMPANY.address}</div>
            <div className="text-xs text-[#666] mt-1">
              Contact: <span className="font-mono text-[#0A0A0A]">{COMPANY.contact1}</span> /
              <span className="font-mono text-[#0A0A0A]"> {COMPANY.contact2}</span> ·
              Customer Care: <span className="font-mono text-[#0A0A0A]"> {COMPANY.customerCare}</span>
            </div>
          </div>
          <div className="text-xs text-[#666] leading-relaxed sm:text-right">
            {COMPANY.email}<br/>
            GST: <span className="font-mono text-[#0A0A0A]">{COMPANY.gst}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
